import React, { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import axios from "axios";
import { COLORS } from "theme/tokens";

function PulseCenter() {
  const [status, setStatus] = useState("IDLE"); 
  const [logs, setLogs] = useState(["[SYSTEM] Pulse Center Ready."]);
  const [leaderboard, setLeaderboard] = useState({
    columns: [
      { Header: "Symbol", accessor: "symbol", width: "15%" },
      { Header: "Elite", accessor: "elite", width: "10%" },
      { Header: "Risk", accessor: "risk", width: "10%" },
      { Header: "Return (%)", accessor: "return", width: "15%" },
      { Header: "Max DD (%)", accessor: "mdd", width: "15%" },
      { Header: "PF", accessor: "pf", width: "10%" },
      { Header: "Trades", accessor: "trades", width: "10%" },
      { Header: "Action", accessor: "action", width: "10%" },
    ],
    rows: []
  });

  const timerRef = useRef(null);
  const logEndRef = useRef(null);

  const scrollToBottom = () => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // 定期獲取狀態
  const fetchStatus = async () => {
    try {
      const res = await axios.get("/api/pulse/status");
      setStatus(res.data.status === "RUNNING" ? "PROCESSING" : res.data.status);
      setLogs(res.data.logs);
      
      if (res.data.status === "COMPLETED") {
        clearInterval(timerRef.current);
        fetchLeaderboard();
      }
    } catch (err) {
      console.error("Status check failed", err);
    }
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState({ 
    strategyName: "",
    logicCode: "",
    params: "",
    kpi: "",
    mcScore: null,
    sharpe: null
  });

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get("/api/pulse/leaderboard");
      if (res.data && res.data.rows) {
        const rowsWithAction = res.data.rows.map(row => ({
          ...row,
          action: (
            <MDButton 
              color="info" 
              size="small" 
              onClick={() => {
                let paramsObj = {};
                let kpiObj = {};
                try { paramsObj = JSON.parse(row.paramsJson || "{}"); } catch(e) {}
                try { kpiObj = JSON.parse(row.kpiTarget || "{}"); } catch(e) {}
                setSelectedDetails({
                  strategyName: `${row.symbol} / ${row.timeframe}`,
                  logicCode: row.logicCode || "(No logic code stored)",
                  params: JSON.stringify(paramsObj, null, 2),
                  kpi: JSON.stringify(kpiObj, null, 2),
                  mcScore: row.mcScore ?? null,
                  sharpe: row.sharpe ?? null,
                  performance: {
                    monthlyReturn: row.monthlyReturn,
                    maxDrawdown: row.maxDrawdown,
                    profitFactor: row.profitFactor,
                    winRate: row.winRate,
                    tradeCount: row.tradeCount,
                    longWinRate: row.longWinRate,
                    shortWinRate: row.shortWinRate
                  }
                });
                setOpenDialog(true);
              }}
            >
              🔎 Inspect
            </MDButton>
          )
        }));
        setLeaderboard(prev => ({ ...prev, rows: rowsWithAction }));
      }
    } catch (err) {
      console.error("Leaderboard fetch failed", err);
    }
  };

  const startPulse = async () => {
    try {
      await axios.post("/api/pulse/start");
      setStatus("PROCESSING");
      setLogs(["[SYSTEM] Initializing Pulse Command..."]);
      timerRef.current = setInterval(fetchStatus, 2000);
    } catch (err) {
      alert("Failed to start pulse pipeline.");
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => clearInterval(timerRef.current);
  }, []);

  const getStepStatus = (stepName) => {
    if (status === "COMPLETED") return "success";
    const logStr = logs.join(" ");
    if (logStr.includes(stepName)) return "info";
    return "grey-200";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ background: "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)", padding: 3 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="h4" color="white" fontWeight="bold">⚡ Beast AI 5.0 Strategy Factory</MDTypography>
                  <MDTypography variant="button" color="white" opacity={0.8}>工業化 5.0：反向工程、基因修補反饋、KNN 型態辨識</MDTypography>
                </MDBox>
                <MDBox display="flex" gap={2} alignItems="center">
                  <select 
                    id="pulse-symbol"
                    style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.2)", color: "white", fontSize: "14px", outline: "none", cursor: "pointer" }}
                  >
                    <option value="ALL">全部幣別依序跑 (ALL)</option>
                    <option value="XAUUSD">XAUUSD (黃金)</option>
                    <option value="EURUSD">EURUSD (歐美)</option>
                    <option value="GBPUSD">GBPUSD (磅美)</option>
                    <option value="USDJPY">USDJPY (美日)</option>
                  </select>
                  <select 
                    id="pulse-timeframe"
                    style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.2)", color: "white", fontSize: "14px", outline: "none", cursor: "pointer" }}
                  >
                    <option value="FULL_PIPELINE">AI 5.0 全自主研發 (M15 高頻邏輯 + 模式辨識)</option>
                    <option value="H1">H1 (通用趨勢戰術包 - 僅優化參數)</option>
                    <option value="M15">M15 (均值回歸戰術包 - 僅優化參數)</option>
                  </select>
                  <MDButton 
                    variant="gradient" 
                    color={status === "IDLE" || status === "COMPLETED" ? "success" : "warning"}
                    size="large"
                    onClick={async () => {
                      const sym = document.getElementById("pulse-symbol").value;
                      const tf = document.getElementById("pulse-timeframe").value;
                      try {
                        await axios.post("/api/pulse/start", { symbol: sym, timeframe: tf });
                        setStatus("PROCESSING");
                        setLogs([`[SYSTEM] Initializing Tactical Package: ${sym} / ${tf}...`]);
                        timerRef.current = setInterval(fetchStatus, 2000);
                      } catch (err) {
                        alert("Failed to start pulse pipeline.");
                      }
                    }}
                    disabled={status === "PROCESSING" || status === "RUNNING"}
                  >
                    <Icon sx={{ mr: 1 }}>rocket_launch</Icon> {status === "PROCESSING" || status === "RUNNING" ? "PULSE RUNNING" : "START PULSE"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium">管道實時進度 (Pipeline Flow)</MDTypography>
                <MDBox mt={4} display="flex" justifyContent="space-between" alignItems="center" position="relative">
                  <MDBox position="absolute" top="50%" left="5%" right="5%" height="2px" bgColor="grey-300" zIndex={0} />
                  
                  {[
                    { label: "MINING", icon: "search", match: "Mining" },
                    { label: "EVOLVING", icon: "psychology", match: "Logic" },
                    { label: "GENERATING", icon: "code", match: "Generating" },
                    { label: "VALIDATING", icon: "fact_check", match: "Validation" }
                  ].map((step, idx) => (
                    <MDBox key={step.label} display="flex" flexDirection="column" alignItems="center" zIndex={1} bgColor="white" p={1}>
                      <MDBox 
                        width="45px" height="45px" borderRadius="50%" 
                        display="flex" justifyContent="center" alignItems="center"
                        bgColor={getStepStatus(step.match)}
                        color="white" shadow="lg" mb={1}
                      >
                        <Icon>{step.icon}</Icon>
                      </MDBox>
                      <MDTypography variant="caption" fontWeight="bold" sx={{ color: getStepStatus(step.match) === "info" ? "#1A73E8" : COLORS.grayDark }}>
                        {step.label}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
              
              <MDBox p={3} mt={1}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>4.0 精英策略排行榜 (Validation Leaderboard)</MDTypography>
                <DataTable table={leaderboard} showTotalEntries={false} isSorted={true} noEndBorder />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", backgroundColor: "#1e1e1e", boxShadow: "inset 0 0 10px #000" }}>
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #333">
                <MDTypography variant="h6" fontWeight="medium" color="white">Tactical Console</MDTypography>
                <MDBox display="flex" alignItems="center">
                   <MDTypography variant="caption" color="white" sx={{ mr: 1 }}>{status}</MDTypography>
                   <MDBox bgColor={status === "COMPLETED" ? "success" : (status === "PROCESSING" ? "warning" : "secondary")} borderRadius="50%" width="10px" height="10px" />
                </MDBox>
              </MDBox>
              <MDBox p={2} sx={{ height: "500px", overflowY: "auto", fontFamily: "'Fira Code', monospace", fontSize: "0.75rem" }}>
                {logs.map((log, i) => (
                  <MDBox key={i} sx={{ mb: 0.5, borderLeft: "2px solid", borderColor: log.includes("[ERROR]") ? "error" : (log.includes("[SYSTEM]") ? "info" : "#444"), pl: 1 }}>
                    <MDTypography variant="caption" color={log.includes("[ERROR]") ? "error" : (log.includes("[SYSTEM]") ? "info" : "white")}>
                      {log}
                    </MDTypography>
                  </MDBox>
                ))}
                <div ref={logEndRef} />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ background: "#1a1a2e", color: "white", borderBottom: "1px solid #333" }}>
          🔬 Strategy Transparency Inspector — {selectedDetails.strategyName}
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#0d0d1a", color: "white", p: 3 }}>
          
          {/* Score Panel */}
          <MDBox display="flex" gap={2} mb={3}>
            {selectedDetails.sharpe !== null && (
              <MDBox sx={{ flex: 1, background: "rgba(0,200,100,0.1)", border: "1px solid rgba(0,200,100,0.3)", borderRadius: "8px", p: 2, textAlign: "center" }}>
                <MDTypography variant="caption" color="white" opacity={0.7}>Sharpe Ratio</MDTypography>
                <MDTypography variant="h4" color="success">{selectedDetails.sharpe?.toFixed(2) ?? "N/A"}</MDTypography>
              </MDBox>
            )}
            {selectedDetails.mcScore !== null && (
              <MDBox sx={{ flex: 1, background: "rgba(100,100,255,0.1)", border: "1px solid rgba(100,100,255,0.3)", borderRadius: "8px", p: 2, textAlign: "center" }}>
                <MDTypography variant="caption" color="white" opacity={0.7}>Monte Carlo Score</MDTypography>
                <MDTypography variant="h4" color="info">{(selectedDetails.mcScore * 100).toFixed(0)}%</MDTypography>
              </MDBox>
            )}
          </MDBox>

          {/* Actual Performance Panel */}
          {selectedDetails.performance && (
            <>
              <MDTypography variant="h6" sx={{ color: "#00d2ff", mb: 1 }}>📊 Actual Performance (Backtest Result):</MDTypography>
              <MDBox display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={3} p={2} sx={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", border: "1px solid #333" }}>
                <MDBox>
                  <MDTypography variant="caption" color="white" opacity={0.6}>Monthly Return</MDTypography>
                  <MDTypography variant="h6" color="success">{selectedDetails.performance.monthlyReturn?.toFixed(2)}%</MDTypography>
                </MDBox>
                <MDBox>
                  <MDTypography variant="caption" color="white" opacity={0.6}>Max Drawdown</MDTypography>
                  <MDTypography variant="h6" color="error">{selectedDetails.performance.maxDrawdown?.toFixed(2)}%</MDTypography>
                </MDBox>
                <MDBox>
                  <MDTypography variant="caption" color="white" opacity={0.6}>Profit Factor</MDTypography>
                  <MDTypography variant="h6" color="info">{selectedDetails.performance.profitFactor?.toFixed(2)}</MDTypography>
                </MDBox>
                <MDBox>
                  <MDTypography variant="caption" color="white" opacity={0.6}>Trade Count</MDTypography>
                  <MDTypography variant="h6" color="white">{selectedDetails.performance.tradeCount}</MDTypography>
                </MDBox>
              </MDBox>
            </>
          )}

          {/* AI Logic Code */}
          <MDTypography variant="h6" sx={{ color: "#7bed9f", mb: 1 }}>📜 Full Python Strategy Script:</MDTypography>
          <pre style={{ margin: 0, padding: "14px", background: "#0a0a0a", borderRadius: "6px", fontSize: "0.8rem", height: "400px", overflow: "auto", border: "1px solid #333", color: "#a8ff78", fontFamily: "'Courier New', monospace", lineHeight: 1.5 }}>
            {selectedDetails.logicCode}
          </pre>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <MDTypography variant="h6" sx={{ color: COLORS.warning, mb: 1 }}>🎯 KPI Targets:</MDTypography>
              <pre style={{ margin: 0, padding: "10px", background: "#0a0a0a", borderRadius: "6px", fontSize: "0.75rem", overflowX: "auto", border: "1px solid #333", color: "#ffd700" }}>
                {selectedDetails.kpi}
              </pre>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="h6" sx={{ color: "#00d2ff", mb: 1 }}>⚙️ Evolved Parameters:</MDTypography>
              <pre style={{ margin: 0, padding: "10px", background: "#0a0a0a", borderRadius: "6px", fontSize: "0.75rem", overflowX: "auto", border: "1px solid #333", color: "#a8ff78" }}>
                {selectedDetails.params}
              </pre>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ background: "#1a1a2e" }}>
          <MDButton onClick={() => setOpenDialog(false)} color="dark">Close</MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default PulseCenter;
